// =============================================================================
// StatsPage — Slice 7e.1 owner-only ops dashboard
// =============================================================================
//
// Single-page admin overlay. Date range picker + headline tiles + monthly
// chart. Owner-gated at the route level; this component also bails for
// non-owners as a backstop.
//
// Tiles arrange in two rows of three on desktop, stacking on mobile:
//
//   [ Bookings count ]   [ Revenue (£) ]   [ Cost (£) ]
//   [ Hours sold ]       [ Hours paid ]    [ Avg £/hr sold | paid ]
//
// Margin row sits below as its own emphasised band. Monthly chart fills
// the bottom — bookings/revenue/cost stacked, easy to eyeball trend.
// =============================================================================

import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
    CartesianGrid,
} from 'recharts';
import { Calendar, TrendingUp, AlertCircle } from 'lucide-react';

import { useApi } from '../lib/api.js';
import { useCurrentUser } from '../hooks/useCurrentUser.js';
import { PageHeader, LoadingPage } from '../components/ui/Feedback.jsx';

// =============================================================================
// Date range presets
// =============================================================================
//
// Compute the [from, to] inclusive date range for each preset relative to
// "today". Pure functions, no React state. Returned as YYYY-MM-DD strings
// since that's what the backend expects.
// =============================================================================

function fmt(d) {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
}

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
function startOfWeek(d) {
    // ISO week — Monday start
    const day = d.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    const out = new Date(d);
    out.setDate(d.getDate() + diff);
    return out;
}
function endOfWeek(d) {
    const s = startOfWeek(d);
    const e = new Date(s);
    e.setDate(s.getDate() + 6);
    return e;
}
function startOfQuarter(d) {
    const q = Math.floor(d.getMonth() / 3);
    return new Date(d.getFullYear(), q * 3, 1);
}
function endOfQuarter(d) {
    const q = Math.floor(d.getMonth() / 3);
    return new Date(d.getFullYear(), q * 3 + 3, 0);
}

const PRESETS = [
    { key: 'this_week',     label: 'This week' },
    { key: 'last_week',     label: 'Last week' },
    { key: 'this_month',    label: 'This month' },
    { key: 'last_month',    label: 'Last month' },
    { key: 'this_quarter',  label: 'This quarter' },
    { key: 'last_quarter',  label: 'Last quarter' },
    { key: 'this_year',     label: 'This year' },
    { key: 'last_year',     label: 'Last year' },
    { key: 'custom',        label: 'Custom…' },
];

function rangeForPreset(key) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    switch (key) {
        case 'this_week':
            return { from: fmt(startOfWeek(today)), to: fmt(endOfWeek(today)) };
        case 'last_week': {
            const lastWeekDay = new Date(today);
            lastWeekDay.setDate(today.getDate() - 7);
            return { from: fmt(startOfWeek(lastWeekDay)), to: fmt(endOfWeek(lastWeekDay)) };
        }
        case 'this_month':
            return { from: fmt(startOfMonth(today)), to: fmt(endOfMonth(today)) };
        case 'last_month': {
            const lastMonthDay = new Date(today.getFullYear(), today.getMonth() - 1, 15);
            return { from: fmt(startOfMonth(lastMonthDay)), to: fmt(endOfMonth(lastMonthDay)) };
        }
        case 'this_quarter':
            return { from: fmt(startOfQuarter(today)), to: fmt(endOfQuarter(today)) };
        case 'last_quarter': {
            const lastQuarterDay = new Date(today);
            lastQuarterDay.setMonth(today.getMonth() - 3);
            return { from: fmt(startOfQuarter(lastQuarterDay)), to: fmt(endOfQuarter(lastQuarterDay)) };
        }
        case 'this_year':
            return {
                from: fmt(new Date(today.getFullYear(), 0, 1)),
                to:   fmt(new Date(today.getFullYear(), 11, 31)),
            };
        case 'last_year':
            return {
                from: fmt(new Date(today.getFullYear() - 1, 0, 1)),
                to:   fmt(new Date(today.getFullYear() - 1, 11, 31)),
            };
        default:
            return null;
    }
}

// =============================================================================
// Formatters
// =============================================================================
function formatGBP(n) {
    if (n == null || isNaN(n)) return '—';
    return new Intl.NumberFormat('en-GB', {
        style: 'currency', currency: 'GBP',
        minimumFractionDigits: 0, maximumFractionDigits: 0,
    }).format(Number(n));
}
function formatGBPPrecise(n) {
    if (n == null || isNaN(n)) return '—';
    return new Intl.NumberFormat('en-GB', {
        style: 'currency', currency: 'GBP',
        minimumFractionDigits: 2, maximumFractionDigits: 2,
    }).format(Number(n));
}
function formatHours(n) {
    if (n == null || isNaN(n)) return '—';
    return `${Number(n).toFixed(1)} hrs`;
}
function formatPct(n) {
    if (n == null || isNaN(n)) return '—';
    return `${Number(n).toFixed(1)}%`;
}
function formatMonth(monthStr) {
    // monthStr arrives as 'YYYY-MM'
    const [yyyy, mm] = monthStr.split('-');
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${months[parseInt(mm, 10) - 1]} '${yyyy.slice(2)}`;
}

// =============================================================================
// Component
// =============================================================================
export default function StatsPage() {
    const api = useApi();
    const { isOwner } = useCurrentUser();

    const [presetKey, setPresetKey] = useState('this_month');
    const [customRange, setCustomRange] = useState(() => {
        const r = rangeForPreset('this_month');
        return r;
    });

    // Resolved range — preset-derived unless 'custom' picked, then customRange
    const range = useMemo(() => {
        if (presetKey === 'custom') return customRange;
        return rangeForPreset(presetKey);
    }, [presetKey, customRange]);

    const { data, isLoading, isError, error } = useQuery({
        queryKey: ['stats', range.from, range.to],
        queryFn: () => api.get(`/stats?from=${range.from}&to=${range.to}`),
        enabled: !!(range?.from && range?.to),
        staleTime: 60_000,   // 1 min — stats don't change second-to-second
    });

    if (!isOwner) {
        return (
            <div className="p-6 max-w-2xl mx-auto text-sm text-ink-subtle">
                Stats are owner-only.
            </div>
        );
    }

    return (
        <div>
            <PageHeader
                title="Stats"
                subtitle="Revenue, cost, and hours reconciliation across the chosen period."
            />

            {/* Date range selector */}
            <div className="card mb-5">
                <div className="card-body">
                    <div className="flex items-center gap-3 flex-wrap">
                        <Calendar className="h-4 w-4 text-ink-subtle flex-shrink-0" />
                        <div className="flex flex-wrap gap-1.5">
                            {PRESETS.map((p) => {
                                const selected = presetKey === p.key;
                                return (
                                    <button
                                        key={p.key}
                                        type="button"
                                        onClick={() => {
                                            setPresetKey(p.key);
                                            if (p.key !== 'custom') {
                                                setCustomRange(rangeForPreset(p.key));
                                            }
                                        }}
                                        className={`px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                                            selected
                                                ? 'border-ink bg-ink text-paper'
                                                : 'border-paper-border bg-paper text-ink hover:bg-canvas/40'
                                        }`}
                                    >
                                        {p.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {presetKey === 'custom' && (
                        <div className="mt-3 flex items-center gap-2 flex-wrap">
                            <label className="text-xs text-ink-subtle">From</label>
                            <input
                                type="date"
                                value={customRange.from}
                                onChange={(e) => setCustomRange((r) => ({ ...r, from: e.target.value }))}
                                className="text-sm border border-paper-border rounded px-2 py-1 bg-paper text-ink focus:outline-none focus:ring-1 focus:ring-ink/30"
                            />
                            <label className="text-xs text-ink-subtle">To</label>
                            <input
                                type="date"
                                value={customRange.to}
                                onChange={(e) => setCustomRange((r) => ({ ...r, to: e.target.value }))}
                                className="text-sm border border-paper-border rounded px-2 py-1 bg-paper text-ink focus:outline-none focus:ring-1 focus:ring-ink/30"
                            />
                        </div>
                    )}

                    <div className="mt-3 text-xs text-ink-subtle">
                        Showing {range.from} to {range.to}
                    </div>
                </div>
            </div>

            {/* Loading / error / data */}
            {isLoading && <LoadingPage label="Loading stats…" />}

            {isError && (
                <div className="card mb-5">
                    <div className="card-body">
                        <div className="flex items-start gap-2 text-sm text-red-700">
                            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                            <div>
                                <div className="font-medium">Couldn't load stats</div>
                                <div className="text-xs text-red-600 mt-0.5">
                                    {error?.message || 'Unknown error'}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {data && !isLoading && (
                <>
                    {/* Headline tiles */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-5">
                        <StatTile
                            label="Bookings"
                            value={data.bookings_count}
                                                        sublabel={
                                data.cancelled_count > 0
                                    ? `rides in period · ${data.cancelled_count} cancelled`
                                    : 'rides in period'
                            }
                        >
                            <ClassBreakdown
                                rows={data.by_vehicle_class}
                                render={(r) => r.bookings_count}
                            />
                            <ClassBreakdown
                                rows={data.by_mode}
                                render={(r) => r.bookings_count}
                            />
                        </StatTile>
                        <StatTile
                            label="Revenue"
                            value={formatGBP(data.customer.revenue)}
                            sublabel="customer fares, ex VAT"
                        >
                            <ClassBreakdown
                                rows={data.by_vehicle_class}
                                render={(r) => formatGBP(r.revenue)}
                            />
                            <ClassBreakdown
                                rows={data.by_mode}
                                render={(r) => formatGBP(r.revenue)}
                            />
                        </StatTile>
                        <StatTile
                            label="Cost"
                            value={formatGBP(data.chauffeur.cost)}
                            sublabel="chauffeur fares, ex VAT"
                        />
                        <StatTile
                            label="Operating costs"
                            value={formatGBP(data.operating_costs)}
                            sublabel="internal spend, ex VAT"
                        />
                       <StatTile
                            label="Hours sold"
                            value={formatHours(data.customer.hours_total)}
                            sublabel={
                                data.customer.avg_per_hour != null
                                    ? `avg ${formatGBPPrecise(data.customer.avg_per_hour)}/hr`
                                    : 'no hours logged'
                            }
                        >
                           <ClassBreakdown
                                rows={data.by_vehicle_class}
                                render={(r) => formatHours(r.customer_hours)}
                            />
                        </StatTile>
                        <StatTile
                            label="Hours paid"
                            value={formatHours(data.chauffeur.hours_total)}
                            sublabel={
                                data.chauffeur.avg_per_hour != null
                                    ? `avg ${formatGBPPrecise(data.chauffeur.avg_per_hour)}/hr`
                                    : 'no hours logged'
                            }
                        />
                        <StatTile
                            label="Hours delta"
                            value={formatHours(
                                Number(data.customer.hours_total) - Number(data.chauffeur.hours_total)
                            )}
                            sublabel="customer − chauffeur"
                        />
                    </div>

                    {/* Margin row — emphasised */}
                    <div className="card mb-5">
                        <div className="card-body">
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                                <div className="flex items-center gap-2">
                                    <TrendingUp className="h-5 w-5 text-ink-subtle" />
                                    <h2 className="text-base font-semibold text-ink">Margin</h2>
                                </div>
                                <div className="flex items-baseline gap-3">
                                    <span className={`text-3xl font-bold ${
                                        Number(data.margin) >= 0 ? 'text-ink' : 'text-red-700'
                                    }`}>
                                        {formatGBP(data.margin)}
                                    </span>
                                    <span className="text-sm text-ink-subtle">
                                        ({formatPct(data.margin_pct)})
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                                        {/* Chauffeur / customer breakdown */}
                    <BreakdownTabs chauffeurs={data.by_chauffeur} customers={data.by_customer} vehicles={data.by_vehicle} />

                    {/* Monthly breakdown chart */}
                    {data.monthly_breakdown && data.monthly_breakdown.length > 0 && (
                        <div className="card">
                            <div className="card-header">
                                <h2 className="text-sm font-semibold text-ink">Monthly breakdown</h2>
                            </div>
                            <div className="card-body">
                                <MonthlyChart data={data.monthly_breakdown} />
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

// =============================================================================
// ClassBreakdown — small per-vehicle-class lines under a headline tile
// =============================================================================
// Renders a muted list of "label … value" rows, one per vehicle class, using
// whichever metric the caller passes via `render`. Hidden if no data.
// =============================================================================
function ClassBreakdown({ rows, render }) {
    if (!rows || rows.length === 0) return null;
    return (
        <div className="mt-3 pt-3 border-t border-paper-border space-y-1">
            {rows.map((r) => (
                <div key={r.vehicle_class || r.mode} className="flex items-baseline justify-between gap-2 text-xs">
                    <span className="text-ink-subtle truncate">{r.label}</span>
                    <span className="text-ink font-medium tabular-nums flex-shrink-0">{render(r)}</span>
                </div>
            ))}
        </div>
    );
}

// =============================================================================
// ChauffeurBreakdown — per-chauffeur hours + rate table
// =============================================================================
// One row per chauffeur: bookings, hours paid, and avg £/hr. The average uses
// hours actually WORKED as the denominator (backend excludes zero-hour
// bookings), so avg_per_hour is null when a chauffeur logged no worked hours —
// formatGBPPrecise renders that as a dash. Hidden entirely if no data.
// =============================================================================
function BreakdownTabs({ chauffeurs, customers, vehicles }) {
    const [tab, setTab] = useState('chauffeurs');
    const tabs = [
        { key: 'chauffeurs', label: 'Chauffeurs', count: chauffeurs?.length || 0 },
        { key: 'customers',  label: 'Customers',  count: customers?.length || 0 },
        { key: 'vehicles',   label: 'Vehicles',   count: vehicles?.length || 0 },
    ];
    return (
        <div className="card mb-5">
            <div className="card-header flex items-center justify-between gap-3 flex-wrap">
                <h2 className="text-sm font-semibold text-ink">Breakdown</h2>
                <div className="flex gap-1 p-1 bg-canvas rounded-lg">
                    {tabs.map((t) => (
                        <button
                            key={t.key}
                            type="button"
                            onClick={() => setTab(t.key)}
                            className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                                tab === t.key ? 'bg-paper text-ink shadow-sm' : 'text-ink-subtle hover:text-ink'
                            }`}
                        >
                            {t.label} <span className="opacity-60">{t.count}</span>
                        </button>
                    ))}
                </div>
            </div>
            <div className="card-body">
                {tab === 'chauffeurs' && <ChauffeurTable rows={chauffeurs} />}
                {tab === 'customers' && <CustomerTable rows={customers} />}
                {tab === 'vehicles' && <VehicleTable rows={vehicles} />}
            </div>
        </div>
    );
}

// =============================================================================
// VehicleTable — own-fleet vehicles: jobs, hours covered, revenue, £/hr
// =============================================================================
// Completed / no-show jobs only. Hours per job = hours worked, else chauffeur
// hours, else customer hours, else booked hours (hourly). Jobs with none of
// those count under "No hours" so gaps are visible; £/hr only uses jobs that
// have hours.
// =============================================================================
function VehicleTable({ rows }) {
    const [openId, setOpenId] = useState(null);
    if (!rows || rows.length === 0) {
        return <p className="text-sm text-ink-subtle">No completed jobs on your own vehicles in this period.</p>;
    }
    const missingTotal = rows.reduce((s, r) => s + (r.missing_hours || 0), 0);
    return (
        <>
            {missingTotal > 0 && (
                <div className="mb-3 text-xs text-status-warning bg-amber-50 border border-amber-200 rounded px-2.5 py-1.5">
                    {missingTotal} job{missingTotal === 1 ? '' : 's'} have no hours recorded, so hours are understated for the rows marked ⚠. Click ⚠ to see them.
                </div>
            )}
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-xs uppercase tracking-wide text-ink-subtle">
                            <th className="text-left font-medium pb-2">Vehicle</th>
                            <th className="text-right font-medium pb-2">Jobs</th>
                            <th className="text-right font-medium pb-2">Hours</th>
                            <th className="text-right font-medium pb-2">Revenue</th>
                            <th className="text-right font-medium pb-2">£/hr</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => {
                            const isOpen = openId === r.vehicle_id;
                            const jobs = r.missing_jobs || [];
                            return [
                                <tr key={r.vehicle_id} className="border-t border-paper-border">
                                    <td className="text-left py-2 text-ink">
                                        <span className="font-medium">{r.registration}</span>
                                        <span className="text-ink-subtle"> · {r.label}</span>
                                        {r.missing_hours > 0 && (
                                            <button
                                                type="button"
                                                onClick={() => setOpenId(isOpen ? null : r.vehicle_id)}
                                                className="ml-1.5 text-xs text-status-warning hover:underline"
                                                title="Show jobs without hours"
                                            >
                                                ⚠ {r.missing_hours}
                                            </button>
                                        )}
                                    </td>
                                    <td className="text-right py-2 text-ink tabular-nums">{r.jobs}</td>
                                    <td className="text-right py-2 text-ink font-medium tabular-nums">{formatHours(r.hours)}</td>
                                    <td className="text-right py-2 text-ink tabular-nums">{formatGBP(r.revenue)}</td>
                                    <td className="text-right py-2 text-ink tabular-nums">{formatGBPPrecise(r.revenue_per_hour)}</td>
                                </tr>,
                                isOpen && jobs.length > 0 && (
                                    <tr key={`${r.vehicle_id}-missing`}>
                                        <td colSpan={5} className="pb-3">
                                            <div className="bg-amber-50 border border-amber-200 rounded px-3 py-2 text-xs">
                                                <div className="text-status-warning font-medium mb-1">Jobs without hours — open and fill in hours worked:</div>
                                                <div className="flex flex-wrap gap-x-4 gap-y-1">
                                                    {jobs.map((j) => (
                                                        <Link key={j.id} to={`/bookings/${j.id}`} className="text-ink underline">
                                                            {j.ride_ref}
                                                            <span className="text-ink-subtle">
                                                                {' · '}{new Date(String(j.pickup_date).slice(0, 10) + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                                                            </span>
                                                        </Link>
                                                    ))}
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                ),
                            ];
                        })}
                    </tbody>
                </table>
            </div>
        </>
    );
}

function ChauffeurTable({ rows }) {
    if (!rows || rows.length === 0) {
        return <p className="text-sm text-ink-subtle">No allocated bookings in this period.</p>;
    }
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm">
                <thead>
                    <tr className="text-xs uppercase tracking-wide text-ink-subtle">
                        <th className="text-left font-medium pb-2">Chauffeur</th>
                        <th className="text-right font-medium pb-2">Bookings</th>
                        <th className="text-right font-medium pb-2">Hours paid</th>
                        <th className="text-right font-medium pb-2">Avg £/hr</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r.chauffeur_id} className="border-t border-paper-border">
                            <td className="text-left py-2 text-ink truncate">{r.label}</td>
                            <td className="text-right py-2 text-ink tabular-nums">{r.bookings_count}</td>
                            <td className="text-right py-2 text-ink tabular-nums">{formatHours(r.hours_paid)}</td>
                            <td className="text-right py-2 text-ink font-medium tabular-nums">{formatGBPPrecise(r.avg_per_hour)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function daysAgo(dateStr) {
    if (!dateStr) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const d = new Date(dateStr);
    d.setHours(0, 0, 0, 0);
    return Math.floor((today - d) / 86_400_000);
}

function formatShortDate(dateStr) {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
}

function CustomerTable({ rows }) {
    const [openId, setOpenId] = useState(null);
    if (!rows || rows.length === 0) {
        return <p className="text-sm text-ink-subtle">No bookings in this period.</p>;
    }
    const missingTotal = rows.reduce((s, r) => s + (r.missing_cost || 0), 0);
    return (
        <>
            {missingTotal > 0 && (
                <div className="mb-3 text-xs text-status-warning bg-amber-50 border border-amber-200 rounded px-2.5 py-1.5">
                    {missingTotal} job{missingTotal === 1 ? '' : 's'} have no chauffeur cost yet, so margin is overstated for the rows marked ⚠. Click ⚠ to see them — add the chauffeur fare, or a £0 line if it was unpaid on purpose.
                </div>
            )}
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-xs uppercase tracking-wide text-ink-subtle">
                            <th className="text-left font-medium pb-2">Customer</th>
                            <th className="text-right font-medium pb-2">Jobs</th>
                            <th className="text-right font-medium pb-2">Revenue</th>
                            <th className="text-right font-medium pb-2">Cost</th>
                            <th className="text-right font-medium pb-2">Margin</th>
                            <th className="text-right font-medium pb-2">Avg job</th>
                            <th className="text-right font-medium pb-2">Share</th>
                            <th className="text-right font-medium pb-2">Last booking</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => {
                            const rowKey = r.customer_id || 'none';
                            const ago = daysAgo(r.last_booking);
                            const quiet = ago != null && ago > 60;
                            const isOpen = openId === rowKey;
                            const jobs = r.missing_jobs || [];
                            return [
                                <tr key={rowKey} className="border-t border-paper-border">
                                    <td className="text-left py-2 text-ink">
                                        {r.label}
                                        {r.missing_cost > 0 && (
                                            <button
                                                type="button"
                                                onClick={() => setOpenId(isOpen ? null : rowKey)}
                                                className="ml-1.5 text-xs text-status-warning hover:underline"
                                                title="Show jobs without chauffeur cost"
                                            >
                                                ⚠ {r.missing_cost}
                                            </button>
                                        )}
                                    </td>
                                    <td className="text-right py-2 text-ink tabular-nums whitespace-nowrap">
                                        {r.jobs}
                                        {r.cancelled > 0 && (
                                            <span className="text-xs text-ink-subtle"> · {r.cancelled} cx</span>
                                        )}
                                    </td>
                                    <td className="text-right py-2 text-ink font-medium tabular-nums">{formatGBP(r.revenue)}</td>
                                    <td className="text-right py-2 text-ink-subtle tabular-nums">{formatGBP(r.cost + r.operating_costs)}</td>
                                    <td className={`text-right py-2 tabular-nums whitespace-nowrap ${r.margin < 0 ? 'text-red-700' : 'text-ink'}`}>
                                        {formatGBP(r.margin)}
                                        <span className="text-xs text-ink-subtle"> {formatPct(r.margin_pct)}</span>
                                    </td>
                                    <td className="text-right py-2 text-ink tabular-nums">{formatGBP(r.avg_job)}</td>
                                    <td className="text-right py-2 text-ink tabular-nums">{formatPct(r.share_pct)}</td>
                                    <td className={`text-right py-2 tabular-nums whitespace-nowrap ${quiet ? 'text-status-warning' : 'text-ink-subtle'}`}>
                                        {formatShortDate(r.last_booking)}
                                    </td>
                                </tr>,
                                isOpen && jobs.length > 0 && (
                                    <tr key={`${rowKey}-missing`}>
                                        <td colSpan={8} className="pb-3">
                                            <div className="bg-amber-50 border border-amber-200 rounded px-3 py-2 text-xs">
                                                <div className="text-status-warning font-medium mb-1">Jobs without chauffeur cost — open and add the chauffeur fare (or £0):</div>
                                                <div className="flex flex-wrap gap-x-4 gap-y-1">
                                                    {jobs.map((j) => (
                                                        <Link key={j.id} to={`/bookings/${j.id}`} className="text-ink underline">
                                                            {j.ride_ref}
                                                            <span className="text-ink-subtle">
                                                                {' · '}{new Date(String(j.pickup_date).slice(0, 10) + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                                                            </span>
                                                        </Link>
                                                    ))}
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                ),
                            ];
                        })}
                    </tbody>
                </table>
            </div>
        </>
    );
}

// =============================================================================
// StatTile — one of six headline numbers
// =============================================================================
function StatTile({ label, value, sublabel, children }) {
    return (
        <div className="card">
            <div className="card-body">
                <div className="text-xs uppercase tracking-wide text-ink-subtle font-medium">
                    {label}
                </div>
                <div className="mt-1 text-2xl font-bold text-ink">{value}</div>
               {sublabel && (
                    <div className="text-xs text-ink-subtle mt-1">{sublabel}</div>
                )}
                {children}
            </div>
        </div>
    );
}

// =============================================================================
// MonthlyChart — recharts grouped bar chart
// =============================================================================
//
// Shows revenue, cost, and margin per month side-by-side. Margin is computed
// inline from revenue − cost (already done backend-side, just plot it).
// Responsive — fills container width, fixed 320px height.
// =============================================================================
function MonthlyChart({ data }) {
    // Prep the data for recharts. Add display label + ensure numbers.
    const chartData = data.map((row) => ({
        month: formatMonth(row.month),
        Revenue: Number(row.revenue),
        Cost: Number(row.cost),
        'Op. costs': Number(row.operating_costs),
        Margin: Number(row.margin),
        bookings: row.bookings_count,
    }));

    return (
        <ResponsiveContainer width="100%" height={320}>
            <BarChart data={chartData} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                <XAxis
                    dataKey="month"
                    tick={{ fontSize: 11, fill: '#666' }}
                    axisLine={{ stroke: 'rgba(0,0,0,0.1)' }}
                />
                <YAxis
                    tick={{ fontSize: 11, fill: '#666' }}
                    axisLine={{ stroke: 'rgba(0,0,0,0.1)' }}
                    tickFormatter={(v) => `£${(v / 1000).toFixed(0)}k`}
                />
                <Tooltip
                    contentStyle={{
                        fontSize: 12,
                        border: '1px solid rgba(0,0,0,0.1)',
                        borderRadius: 4,
                        background: '#fff',
                    }}
                    formatter={(value, name) => [formatGBPPrecise(value), name]}
                    labelFormatter={(label) => label}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Revenue" fill="#0a0a0a" radius={[2, 2, 0, 0]} />
                <Bar dataKey="Cost" fill="#737373" radius={[2, 2, 0, 0]} />
                <Bar dataKey="Op. costs" fill="#f59e0b" radius={[2, 2, 0, 0]} />
                <Bar dataKey="Margin" fill="#10b981" radius={[2, 2, 0, 0]} />
            </BarChart>
        </ResponsiveContainer>
    );
}
